import React, { useState } from 'react'
import { useQuizStore } from '../store/quizStore'
import { Check } from 'lucide-react'

interface Option {
  label: string
  value: string
  emoji?: string
}

interface QuestionPageProps {
  pageNum: number
  question: string
  subtext?: string
  options: Option[]
  singleSelect: boolean
  onNext: (answer: string | string[]) => void
}

export const QuestionPage: React.FC<QuestionPageProps> = ({
  pageNum,
  question,
  subtext,
  options,
  singleSelect,
  onNext,
}) => {
  const [selected, setSelected] = useState<string | string[]>(singleSelect ? '' : [])
  const recordAnswer = useQuizStore((state) => state.recordAnswer)

  const handleSelect = (value: string) => {
    if (singleSelect) {
      setSelected(value)
      // Auto-advance for single-select
      recordAnswer(pageNum, value)
      onNext(value)
    } else {
      setSelected((prev) => {
        const arr = Array.isArray(prev) ? prev : []
        return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
      })
    }
  }

  const handleNext = () => {
    recordAnswer(pageNum, selected)
    onNext(selected)
  }

  const isSelected = (value: string) => {
    if (singleSelect) return selected === value
    return Array.isArray(selected) && selected.includes(value)
  }

  const canProceed = singleSelect ? selected !== '' : Array.isArray(selected) && selected.length > 0

  return (
    <div className="quiz-question-page">
      <div className="quiz-container">
        <div className="quiz-header">
          <h2>{question}</h2>
          {subtext && <p className="subtext">{subtext}</p>}
        </div>

        <div className="options-grid">
          {options.map((option) => (
            <button
              key={option.value}
              className={`option-card ${isSelected(option.value) ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              {isSelected(option.value) && (
                <Check
                  size={20}
                  color="#4CAF50"
                  strokeWidth={3}
                  style={{
                    marginRight: '12px',
                    flexShrink: 0,
                    minWidth: '20px'
                  }}
                />
              )}
              <span style={{ flex: 1 }}>{option.label}</span>
            </button>
          ))}
        </div>

        {!singleSelect && (
          <div className="button-group">
            <button
              className="btn-primary"
              onClick={handleNext}
              disabled={!canProceed}
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
